/**
 * Conversation Service - Firestore Persistence
 * 
 * Handles saving and loading conversations from Firestore.
 * Conversations are stored in: superjarvis_users/{userId}/conversations
 * Messages are stored in: superjarvis_users/{userId}/conversations/{conversationId}/messages
 */
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  addDoc,
  query,
  orderBy,
  getDocs,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';

// ============================================================================
// Types
// ============================================================================

export interface ConversationMessage {
  id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  image?: string;
  agent?: string;
  model?: string;
  tier?: string;
  timestamp: Timestamp;
  sources?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  viewRecipe?: Record<string, any>;
  metadata?: {
    confidence?: number;
    vision_used?: boolean;
    sources?: string[];
    lane_contributions?: Record<string, number>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    view_recipe?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pay_request?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    post_booking_connections?: any;
  };
}

export interface Conversation {
  id: string;
  title: string;
  preview: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  pinned: boolean;
  messageCount: number;
  lastAgent?: string;
}

// ============================================================================
// Firestore Deserialization Sanitizer
// ============================================================================

/**
 * Safely parse a value that may be a JSON string or already an object/null.
 * Returns null if parsing fails.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function safeParseJSON(value: any): any {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Parse a monetary amount that may arrive as a number or a currency-prefixed
 * string (e.g. "C$375.0", "$289", "375").  Strips all non-numeric chars
 * (except dots/commas used as decimal/thousand separators) before calling
 * parseFloat so the SuperJarvisPayCard guard never sees NaN.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseMonetaryAmount(val: any): number {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const cleaned = val.replace(/[^0-9.,]/g, '').replace(/,/g, '');
    const n = parseFloat(cleaned);
    return isNaN(n) ? 0 : n;
  }
  return 0;
}

/**
 * Sanitize a message's metadata loaded from Firestore.
 * Firestore deserialization can produce type mismatches (strings instead of
 * objects, objects-with-numeric-keys instead of arrays, etc.).
 * This function normalizes all metadata fields to their expected types so
 * the React render pipeline never encounters unexpected shapes.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function sanitizeMessageMetadata(metadata: any): ConversationMessage['metadata'] {
  if (!metadata || typeof metadata !== 'object') return undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sanitized: Record<string, any> = { ...metadata };

  // --- view_recipe ---
  let viewRecipe = metadata.view_recipe;
  // If it's a string, attempt to parse it
  if (typeof viewRecipe === 'string') {
    viewRecipe = safeParseJSON(viewRecipe);
  }
  if (viewRecipe !== null && typeof viewRecipe === 'object') {
    // Ensure blocks is an Array
    if ('blocks' in viewRecipe) {
      if (!Array.isArray(viewRecipe.blocks)) {
        // Could be a Firestore object-with-numeric-keys; try Object.values
        if (viewRecipe.blocks !== null && typeof viewRecipe.blocks === 'object') {
          viewRecipe = { ...viewRecipe, blocks: Object.values(viewRecipe.blocks) };
        } else {
          viewRecipe = { ...viewRecipe, blocks: undefined };
        }
      }
    }
    sanitized.view_recipe = viewRecipe;
  } else {
    sanitized.view_recipe = null;
  }

  // --- pay_request ---
  let payRequest = metadata.pay_request;
  if (typeof payRequest === 'string') {
    payRequest = safeParseJSON(payRequest);
  }
  if (payRequest !== null && typeof payRequest === 'object') {
    // Ensure trust_drip_connections is an Array
    if ('trust_drip_connections' in payRequest) {
      if (!Array.isArray(payRequest.trust_drip_connections)) {
        if (payRequest.trust_drip_connections !== null && typeof payRequest.trust_drip_connections === 'object') {
          payRequest = { ...payRequest, trust_drip_connections: Object.values(payRequest.trust_drip_connections) };
        } else {
          payRequest = { ...payRequest, trust_drip_connections: [] };
        }
      }
    }
    // Ensure finalAmount is a number — strip currency symbols before parsing
    // (AI/Firestore may return "C$375.0", "$289", or plain "375")
    if ('finalAmount' in payRequest) {
      const fa = payRequest.finalAmount;
      if (typeof fa !== 'number') {
        payRequest = { ...payRequest, finalAmount: parseMonetaryAmount(fa) };
      }
    }
    // Ensure originalAmount is a number with the same currency-aware parsing
    if ('originalAmount' in payRequest) {
      const oa = payRequest.originalAmount;
      if (typeof oa !== 'number') {
        payRequest = { ...payRequest, originalAmount: parseMonetaryAmount(oa) };
      }
    }
    sanitized.pay_request = payRequest;
  } else {
    sanitized.pay_request = null;
  }

  // --- post_booking_connections ---
  let postBooking = metadata.post_booking_connections;
  if (typeof postBooking === 'string') {
    postBooking = safeParseJSON(postBooking);
  }
  sanitized.post_booking_connections = (postBooking !== null && typeof postBooking === 'object') ? postBooking : null;

  return sanitized as ConversationMessage['metadata'];
}

// ============================================================================
// Conversation Management
// ============================================================================

/**
 * Create a new conversation
 */
export async function createConversation(
  userId: string,
  firstMessage: string
): Promise<string> {
  const conversationsRef = collection(db, 'superjarvis_users', userId, 'conversations');
  
  // Generate a title from the first message (first 50 chars or first sentence)
  const title = generateTitle(firstMessage);
  
  const conversationData = {
    title,
    preview: firstMessage.substring(0, 100),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    pinned: false,
    messageCount: 0
  };
  
  const docRef = await addDoc(conversationsRef, conversationData);
  console.log('[Conversations] Created new conversation:', docRef.id);
  
  return docRef.id;
}

/**
 * Get a conversation by ID
 */
export async function getConversation(
  userId: string,
  conversationId: string
): Promise<Conversation | null> {
  try {
    const convRef = doc(db, 'superjarvis_users', userId, 'conversations', conversationId);
    const convDoc = await getDoc(convRef);
    
    if (!convDoc.exists()) {
      return null;
    }
    
    return {
      id: convDoc.id,
      ...convDoc.data()
    } as Conversation;
  } catch (error) {
    console.error('[Conversations] Error getting conversation:', error);
    return null;
  }
}

/**
 * Update conversation metadata
 */
export async function updateConversation(
  userId: string,
  conversationId: string,
  updates: Partial<Omit<Conversation, 'id' | 'createdAt'>>
): Promise<void> {
  try {
    const convRef = doc(db, 'superjarvis_users', userId, 'conversations', conversationId);
    await updateDoc(convRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('[Conversations] Error updating conversation:', error);
  }
}

// ============================================================================
// Message Management
// ============================================================================

/**
 * Add a message to a conversation
 */
export async function addMessage(
  userId: string,
  conversationId: string,
  message: Omit<ConversationMessage, 'id' | 'timestamp'>
): Promise<string> {
  const messagesRef = collection(
    db, 
    'superjarvis_users', 
    userId, 
    'conversations', 
    conversationId, 
    'messages'
  );
  
  const messageData = {
    ...message,
    timestamp: serverTimestamp()
  };
  
  const docRef = await addDoc(messagesRef, messageData);
  
  // Update conversation metadata
  const convRef = doc(db, 'superjarvis_users', userId, 'conversations', conversationId);
  const convDoc = await getDoc(convRef);
  const currentCount = convDoc.data()?.messageCount || 0;
  
  const updateData: Record<string, unknown> = {
    messageCount: currentCount + 1,
    updatedAt: serverTimestamp()
  };
  
  // Update preview with latest assistant message
  if (message.role === 'assistant') {
    updateData.preview = message.content.substring(0, 100);
    if (message.agent) {
      updateData.lastAgent = message.agent;
    }
  }
  
  await updateDoc(convRef, updateData);
  
  return docRef.id;
}

/**
 * Get all messages in a conversation.
 * Applies defensive sanitization so components never receive unexpected types
 * from Firestore deserialization.
 */
export async function getMessages(
  userId: string,
  conversationId: string
): Promise<ConversationMessage[]> {
  try {
    const messagesRef = collection(
      db, 
      'superjarvis_users', 
      userId, 
      'conversations', 
      conversationId, 
      'messages'
    );
    
    const q = query(messagesRef, orderBy('timestamp', 'asc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map((doc: { id: string; data: () => Record<string, unknown> }) => {
      const data = doc.data();
      // Ensure content is always a string (Firestore may return undefined)
      const content = typeof data.content === 'string' ? data.content : '';
      // Sanitize metadata fields to guard against type mismatches
      const metadata = sanitizeMessageMetadata(data.metadata);
      return {
        id: doc.id,
        ...data,
        content,
        metadata,
      };
    }) as ConversationMessage[];
  } catch (error) {
    console.error('[Conversations] Error getting messages:', error);
    return [];
  }
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Generate a title from the first message
 */
function generateTitle(message: string): string {
  // Remove extra whitespace
  const cleaned = message.trim().replace(/\s+/g, ' ');
  
  // If it's a question, use the question as the title
  const questionMatch = cleaned.match(/^[^.!?]*\?/);
  if (questionMatch && questionMatch[0].length <= 60) {
    return questionMatch[0];
  }
  
  // Otherwise, use the first sentence or first 50 characters
  const firstSentence = cleaned.match(/^[^.!?]+[.!?]/);
  if (firstSentence && firstSentence[0].length <= 60) {
    return firstSentence[0];
  }
  
  // Fallback: first 50 characters with ellipsis
  if (cleaned.length <= 50) {
    return cleaned;
  }
  
  // Cut at word boundary
  const truncated = cleaned.substring(0, 50);
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > 30) {
    return truncated.substring(0, lastSpace) + '...';
  }
  
  return truncated + '...';
}

/**
 * Save a complete conversation (for importing or syncing)
 */
export async function saveFullConversation(
  userId: string,
  conversationId: string,
  messages: Omit<ConversationMessage, 'id' | 'timestamp'>[]
): Promise<void> {
  for (const message of messages) {
    await addMessage(userId, conversationId, message);
  }
}
